/**
 * OnboardingGuard — wraps protected routes so authenticated users land
 * on /onboarding/choose-path until Organization.has_chosen_subscription_path
 * flips to true (set by either start-trial or paid-purchase success).
 *
 * Placement: wrap inside <ProtectedRoute> (auth check) but outside the
 * page component, so an unauthenticated user still gets bounced to
 * /login (handled by ProtectedRoute) BEFORE we ever ask for
 * onboarding status.
 *
 * Exemptions: a few routes need to stay reachable during onboarding —
 * the choose-path page itself, the Shop (where the user might purchase
 * a plan), Profile, and Logout. These bypass the guard.
 *
 * Embed / partner-SSO sessions skip the guard entirely — partner-
 * onboarded orgs have has_chosen=true seeded by the migration, and
 * an embed user should never be redirected away from /embed/* anyway.
 */
import { Navigate, useLocation } from "react-router-dom";
import { Spin } from "antd";
import { useAuth } from "../../context/AuthContext";
import { useOnboardingStatus } from "../../hooks/useOnboardingStatus";
import { isPartnerSsoSession } from "../../utils/embedFlow";

// Routes that stay accessible while onboarding is pending. Keep this
// list tight — every entry here is a place where the user can still
// act on the choose-path decision (or undo it). The match is a path
// prefix check on the URL pathname.
const ONBOARDING_EXEMPT_PREFIXES = [
  "/onboarding",
  "/shop",
  "/usage", // read-only diagnostic; reachable during onboarding too
  "/my-account-profile-mv", // existing Profile route
  "/logout",
  "/embed", // partner-SSO iframe — never gate it
];

const OnboardingGuard = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  const { status, isLoading, error } = useOnboardingStatus();

  // No authenticated user → let ProtectedRoute (outer) handle the
  // bounce to /login. We just render children; the outer guard takes
  // priority.
  if (!user) return children;

  // Partner-SSO sessions: skip entirely. Partner-onboarded orgs were
  // seeded with has_chosen=true via the migration, and an embed user
  // must never get yanked out of /embed/* by us.
  if (isPartnerSsoSession()) return children;

  // Exempt routes (choose-path itself, shop, profile, logout, embed).
  if (
    ONBOARDING_EXEMPT_PREFIXES.some((prefix) =>
      location.pathname.startsWith(prefix),
    )
  ) {
    return children;
  }

  // Still loading the status → render a tiny full-screen spinner.
  // Avoids a flash of dashboard before the redirect.
  if (isLoading) {
    return (
      <div
        style={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  // BE error reaching /onboarding/status → fail open. Worst case: a
  // legacy user briefly sees the dashboard. Better than locking
  // everyone out of the product on a transient API blip.
  if (error) return children;

  // Path not yet chosen → redirect to choose-path.
  if (status && status.has_chosen_subscription_path === false) {
    return <Navigate to="/onboarding/choose-path" replace />;
  }

  return children;
};

export default OnboardingGuard;
